import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  Text,
  HStack,
  Circle,
  LinkBox,
  LinkOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScenariosInfinite } from '../hooks/useScenarios';
import { ScenarioFormModal } from '../components/ScenarioFormModal';

export function ScenariosPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createModal = useDisclosure();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useScenariosInfinite(search);
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="lg" color="gray.800">
          {t('scenarios.title')}
        </Heading>
        <Button colorScheme="brand" leftIcon={<AddIcon />} onClick={createModal.onOpen}>
          {t('scenarios.create')}
        </Button>
      </Flex>

      <InputGroup maxW="320px" mb={5}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={t('scenarios.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </InputGroup>

      {isLoading ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height="96px" borderRadius="lg" />
          ))}
        </SimpleGrid>
      ) : items.length === 0 ? (
        <Flex direction="column" align="center" py={20} textAlign="center">
          <Text fontSize="lg" fontWeight="600" color="gray.600">
            {t('scenarios.empty')}
          </Text>
          <Text fontSize="sm" color="gray.400" mt={1}>
            {t('scenarios.emptyDescription')}
          </Text>
        </Flex>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {items.map((s) => (
              <LinkBox
                key={s.id}
                p={5}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
                _hover={{ borderColor: 'brand.300', shadow: 'sm' }}
                transition="all 0.15s"
              >
                <HStack justify="space-between" mb={1}>
                  <LinkOverlay as={RouterLink} to={`/scenarios/${s.id}`}>
                    <Text fontWeight="600" color="gray.800" noOfLines={1}>
                      {s.name}
                    </Text>
                  </LinkOverlay>
                  {s.isActive && <Circle size="10px" bg="green.400" title={t('scenarios.active')} />}
                </HStack>
                {s.description && (
                  <Text fontSize="sm" color="gray.500" noOfLines={2}>
                    {s.description}
                  </Text>
                )}
                <Text fontSize="xs" color="gray.400" mt={2}>
                  {t('scenarios.playlistCount', { count: s.playlistCount })}
                </Text>
              </LinkBox>
            ))}
          </SimpleGrid>
          {hasNextPage && (
            <Flex justify="center" mt={6}>
              <Button variant="outline" onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>
                {t('playlists.loadMore')}
              </Button>
            </Flex>
          )}
        </>
      )}

      <ScenarioFormModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onSaved={(s) => navigate(`/scenarios/${s.id}`)}
      />
    </Box>
  );
}

export default ScenariosPage;
